/**
 * stores/registry.ts
 *
 * Registry state management store
 * Wraps the Dexie 'registry' key-value table with CRUD operations and a reactive cache
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useDatabase } from '@/composables/useDatabase'
import { driveFilesGet } from '@/services/googleApi'
import { logger } from '@/utils/logger'
import type { Registry } from '@/types/google'

// Well-known registry keys
export const MY_DRIVE_ROOT_ID_KEY = 'myDriveRootID'
export const HAS_COMPLETED_ONBOARDING_KEY = 'hasCompletedOnboarding'
export const WATCH_ON_REPLY_KEY = 'watchOnReply'

export const useRegistryStore = defineStore('registry', () => {
    // State
    const registryMap = ref<Map<string, Registry>>(new Map())

    /**
     * Load all registry entries from Dexie into the reactive cache
     */
    async function loadRegistry (forceReload = false) {
        if (registryMap.value.size > 0 && !forceReload) {
            return
        }

        try {
            const db = useDatabase()
            const entries = await db.registry.toArray()
            registryMap.value = new Map(entries.map(entry => [entry.key, entry]))
        } catch (error) {
            logger.error('Error loading registry:', error)
            throw error
        }
    }

    /**
     * Get a single registry entry, reading from the cache first and falling back to Dexie
     */
    async function getEntry (key: string): Promise<Registry | undefined> {
        const cached = registryMap.value.get(key)
        if (cached) {
            return cached
        }

        try {
            const db = useDatabase()
            const entry = await db.registry.get(key)
            if (entry) {
                registryMap.value.set(key, entry)
            }
            return entry
        } catch (error) {
            logger.error('Error getting registry entry:', error)
            throw error
        }
    }

    /**
     * Create or update a registry entry
     */
    async function setEntry (key: string, value: Registry['value'], data?: string): Promise<void> {
        try {
            const db = useDatabase()
            const entry: Registry = { key, value, data }
            await db.registry.put(entry)
            registryMap.value.set(key, entry)
        } catch (error) {
            logger.error('Error setting registry entry:', error)
            throw error
        }
    }

    /**
     * Delete a registry entry
     */
    async function deleteEntry (key: string): Promise<void> {
        try {
            const db = useDatabase()
            await db.registry.delete(key)
            registryMap.value.delete(key)
        } catch (error) {
            logger.error('Error deleting registry entry:', error)
            throw error
        }
    }

    /**
     * Clear the reactive cache (e.g. on logout); does not delete Dexie data
     */
    function clearRegistry () {
        registryMap.value = new Map()
    }

    /**
     * Ensure the `myDriveRootID` registry entry is populated, fetching it from the Drive API
     * (files.get on the well-known 'root' alias, minimal fields) if it isn't already stored
     */
    async function ensureMyDriveRootId (): Promise<string> {
        const existing = await getEntry(MY_DRIVE_ROOT_ID_KEY)
        if (existing && typeof existing.value === 'string') {
            return existing.value
        }

        const file = await driveFilesGet('root', 'id,name')
        if (!file.id) {
            throw new Error('Drive API did not return an id for the "root" file')
        }

        await setEntry(MY_DRIVE_ROOT_ID_KEY, file.id)
        return file.id
    }

    /**
     * Whether the user has finished (or skipped) the onboarding. On the first run with this local database
     * the status is initialised: it is `false` for a new user, `true` for one who already follows folders.
     */
    async function getHasCompletedOnboarding (): Promise<boolean> {
        const existing = await getEntry(HAS_COMPLETED_ONBOARDING_KEY)
        if (typeof existing?.value === 'boolean') {
            return existing.value
        }

        const hasFolders = (await useDatabase().folders.count()) > 0
        await setEntry(HAS_COMPLETED_ONBOARDING_KEY, hasFolders)
        return hasFolders
    }

    async function setHasCompletedOnboarding (completed: boolean): Promise<void> {
        await setEntry(HAS_COMPLETED_ONBOARDING_KEY, completed)
    }

    /**
     * Setting: start watching a comment thread when I reply to it. Defaults to `false`.
     */
    async function getWatchOnReply (): Promise<boolean> {
        const existing = await getEntry(WATCH_ON_REPLY_KEY)
        if (typeof existing?.value === 'boolean') {
            return existing.value
        }

        await setEntry(WATCH_ON_REPLY_KEY, false)
        return false
    }

    async function setWatchOnReply (enabled: boolean): Promise<void> {
        await setEntry(WATCH_ON_REPLY_KEY, enabled)
    }

    return {
        // State
        registryMap,
        // Actions
        loadRegistry,
        getEntry,
        setEntry,
        deleteEntry,
        clearRegistry,
        ensureMyDriveRootId,
        getHasCompletedOnboarding,
        setHasCompletedOnboarding,
        getWatchOnReply,
        setWatchOnReply,
    }
})
